import { Button, Divider } from '@vrplatform/components';
import { MantineReactTable, useLazyTable } from '@vrplatform/table';
import { Box, Group, useMantineTheme } from '@mantine/core';
import { hasValue } from '@vrplatform/utils';
import type { ProfitAndLossJournalEntriesArgs } from './ProfitAndLossTableCell';
import { ProfitAndLossTableFilter } from './ProfitAndLossTableFilter';
import { PROFIT_AND_LOSS_MAX_DEPTH } from './_utils';
import { useProfitAndLossTableColumns } from './useProfitAndLossTableColumns';
import { useProfitAndLossTableQuery } from './useProfitAndLossTableQuery';

export const ProfitAndLossTable = ({
  onOpenJournalEntries,
}: {
  onOpenJournalEntries?: (args: ProfitAndLossJournalEntriesArgs) => void;
}) => {
  const queryData = useProfitAndLossTableQuery();
  const rows = queryData.data?.rows ?? [];

  const theme = useMantineTheme();

  const columns = useProfitAndLossTableColumns({
    columns: queryData.data?.columns ?? [],
    rows: queryData.data?.rows ?? [],
    onOpenJournalEntries,
  });

  const table = useLazyTable({
    data: {
      error: queryData.error?.message,
      loading: queryData.isLoading,
      rows,
      rowCount: rows.length,
    },
    table: {
      columns,
      hideTopBar: true,
      hidePagination: true,
      columnOrder: columns
        .map((c) => c.id || c.accessorKey || c.header)
        .filter(hasValue),
    },
    subRows: {
      getRowCanExpand: (row) => (row.subRows?.length ?? 0) > 0,
      getSubRows: (row) => row.subRows,
      defaultExpanded: true,
      columnWidth: 60,
      allowExpandingAll: false,
      getRowBackgroundColor: (row) => {
        const bg = theme.colors.gray[PROFIT_AND_LOSS_MAX_DEPTH - row.depth];

        if (bg) {
          return `${theme.colors.gray[PROFIT_AND_LOSS_MAX_DEPTH - row.depth]}!important`;
        }

        return `${theme.white}!important`;
      },
    },
    pinned: ['mrt-row-expand', 'title'],
  });

  return (
    <Box
      sx={{
        '--mrt-pinned-column-background-color': 'initial',
        '--table-highlight-on-hover-color': 'initial',
        '--mrt-row-hover-background-color': 'initial',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Group justify="space-between">
        <Group>
          <ProfitAndLossTableFilter.Date />
          <ProfitAndLossTableFilter.Book />
          <ProfitAndLossTableFilter.Listings />
          <ProfitAndLossTableFilter.Party />
          <Divider orientation="vertical" />
          <ProfitAndLossTableFilter.View />
        </Group>

        <Group>
          <ProfitAndLossTableFilter.Type />

          <Divider orientation="vertical" />

          <Button
            variant="transparent"
            leftIcon="CollapseIcon"
            leftIconSize={14}
            onClick={() => table.toggleAllRowsExpanded()}
          >
            Collapse all
          </Button>
        </Group>
      </Group>
      <Divider mt="sm" />
      <MantineReactTable table={table} />
    </Box>
  );
};
